import {
  ReactElement,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import useAnswerRules from "../../../hooks/useAnswerRules";
import { AnswerType, AnswerValueType } from "../../../types/Answer";
import { QuestionType, QuestionTypeEnum } from "../../../types/Question";
import { QuestionnaireSession, SectionIntroType } from "../../../types/Questionnaire";
import { RichContentData } from "../../../types/richContent";
import Question from "../../molecules/Question/Question";
import ContentRenderer from "../ContentRenderer/ContentRenderer";
import Container from "../../atoms/Container";
import NextPrevButtons from "../../molecules/NextPrevButtons";
import useVocalisation from "../../../hooks/useVocalisation";
import {
  answerKeyOf,
  calculateQuestionnaireScore,
  getNextQuestion,
  isQuestionnaireDone,
  countFlowQuestions,
} from "../../../helper/questionnaire";
import { hasAsweredFunctionsByType, defaultAnswerValueByType } from "../../../helper/answer";
import useToast from "../../../hooks/useToast";
import { useTranslation } from "react-i18next";
import { useQuestionnaireCompletionFeedback } from "../ResonanceContext/QuestionnaireCompletionFeedback";
import { useResonanceLayout } from "../ResonanceContext/ResonanceLayoutContext";
import styled from "styled-components";
import React from "react";

/** Delay before a single-choice answer auto-advances, so the pick stays visible. */
const AUTO_ADVANCE_DELAY_MS = 350;

interface DisplayQuestionsProps {
  questionnaireTitle: string;
  session: QuestionnaireSession;
  onSessionChange: (
    updater: QuestionnaireSession | ((prev: QuestionnaireSession) => QuestionnaireSession)
  ) => void;
  onSubmitAnswer: (question: QuestionType, answer: AnswerType) => Promise<void>;
  onQuestionnaireDone?: (totalScore: number | null) => void | Promise<void>;
  onNextClick(): void;
  onPrevClick(): void;
}

const Questionnaire = ({
  questionnaireTitle,
  session,
  onSessionChange,
  onSubmitAnswer,
  onQuestionnaireDone,
  onNextClick,
  onPrevClick,
}: DisplayQuestionsProps): ReactElement => {
  const { questions, answers, history, dependencies, sections } = session;
  const { showCompletionFeedback } = useQuestionnaireCompletionFeedback();
  const { setResonanceLayout } = useResonanceLayout();
  // The respondent's chosen language (Resonance picker) — drives locale-aware ordinals.
  const { i18n } = useTranslation();

  useEffect(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: true,
      backgroundColor: "white",
      title: questionnaireTitle,
    });

    // return () =>
    //   setResonanceLayout({
    //     disableHeader: false,
    //     disableNav: false,
    //     backgroundColor: "white",
    //     title: questionnaireTitle,
    //   });
  }, []);

  const currentQuestion: QuestionType = history[history.length - 1];
  const present = useToast();

  // Does this questionnaire use follow-up sets? Gates the set-aware engine so
  // questionnaires without sets keep their exact original behavior.
  const hasSets = useMemo(
    () => questions.some((q) => q.set_driver_question_id != null),
    [questions]
  );

  // The intro of the section the current question belongs to (null = section-less).
  // Shown as a header above the question; constant while moving within a section.
  // INFORMATION pages are standalone full-page content: never stack a section card
  // above them (they also can't be assigned a section in Diapason).
  const currentSectionIntro = useMemo((): SectionIntroType | null => {
    if (currentQuestion?.type === QuestionTypeEnum.INFORMATION) return null;
    const sectionId = currentQuestion?.section_id ?? null;
    if (!sectionId || !sections?.length) return null;
    return sections.find((section) => section.id === sectionId)?.intro ?? null;
  }, [currentQuestion, sections]);

  // Auto-advance plumbing: a pending timer + an always-fresh goToNextQuestion.
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const goToNextRef = useRef<() => void>(() => {});

  // Where this question's answer lives in the session: its own slot for a repeated
  // instance, the plain (null) slot for top-level and "ask once" questions.
  const currentAnswerKey = answerKeyOf(currentQuestion);

  const currentAnswer: AnswerType | undefined = useMemo(
    () =>
      answers.find(
        (ans) =>
          ans.questionId === currentQuestion.id &&
          (ans.instanceKey ?? null) === currentAnswerKey
      ),
    [answers, currentQuestion.id, currentAnswerKey]
  );

  // Set instances are answered on demand — seed a default answer entry the first
  // time an instance is shown so its value is well-defined.
  useEffect(() => {
    if (currentAnswer) return;
    onSessionChange((prev) => {
      const exists = prev.answers.some(
        (a) => a.questionId === currentQuestion.id && (a.instanceKey ?? null) === currentAnswerKey
      );
      if (exists) return prev;
      return {
        ...prev,
        answers: [
          ...prev.answers,
          {
            questionId: currentQuestion.id,
            questionType: currentQuestion.type as QuestionTypeEnum,
            value: defaultAnswerValueByType[currentQuestion.type as QuestionTypeEnum],
            instanceKey: currentAnswerKey ?? undefined,
          },
        ],
      };
    });
  }, [currentAnswer, currentQuestion, currentAnswerKey, onSessionChange]);

  const { translatedRuleMsg, isAnswerValid } = useAnswerRules(
    currentQuestion,
    currentAnswer?.value
  );

  const changeAnswer = useCallback(
    (answer: AnswerValueType): void => {
      onSessionChange((prev) => {
        const matches = (a: AnswerType): boolean =>
          a.questionId === currentQuestion.id && (a.instanceKey ?? null) === currentAnswerKey;
        const value =
          currentQuestion.type === "SLIDER" && answer === null ? 0 : answer;
        const newAnswers = prev.answers.some(matches)
          ? prev.answers.map((a) => (matches(a) ? { ...a, value } : a))
          : [
              ...prev.answers,
              {
                questionId: currentQuestion.id,
                questionType: currentQuestion.type as QuestionTypeEnum,
                value,
                instanceKey: currentAnswerKey ?? undefined,
              },
            ];
        return { ...prev, answers: newAnswers };
      });

      // Auto-advance for single-choice (radio) questions: once the user picks a
      // valid answer, move on after a short delay so the selection is visible.
      // Re-selecting resets the timer; deselecting (NaN/empty) cancels it.
      if (autoAdvanceTimer.current) {
        clearTimeout(autoAdvanceTimer.current);
        autoAdvanceTimer.current = null;
      }
      const autoAdvances =
        currentQuestion.type === QuestionTypeEnum.RADIOBUTTON ||
        currentQuestion.type === QuestionTypeEnum.DROPDOWN;
      if (
        autoAdvances &&
        hasAsweredFunctionsByType[currentQuestion.type as QuestionTypeEnum](answer)
      ) {
        autoAdvanceTimer.current = setTimeout(() => {
          autoAdvanceTimer.current = null;
          goToNextRef.current();
        }, AUTO_ADVANCE_DELAY_MS);
      }
    },
    [
      currentQuestion.id,
      currentQuestion.type,
      currentAnswerKey,
      onSessionChange,
    ]
  );

  const goToPrevQuestion = useCallback((): void => {
    let sliceValue = -1;
    let choseAnswerWithDependency = null;

    if (dependencies && dependencies.length) {
      const possiblePreviousQuestions = dependencies.filter((dependency) =>
        dependency.answers?.find(
          (answer) =>
            answer.next_question &&
            answer.next_question.id === currentQuestion.id
        )
      );

      if (possiblePreviousQuestions.length) {
        const mostRecentAnsweredQuestion = possiblePreviousQuestions.reduce(
          (max, obj) => (obj.order > max.order ? obj : max)
        );
        const previousQuestionIndex = history.findIndex(
          (item) => item.id === mostRecentAnsweredQuestion.id
        );

        choseAnswerWithDependency = mostRecentAnsweredQuestion.answers.find(
          (answer) =>
            answers.some(
              (sessionAnswer) =>
                sessionAnswer.value === answer.id &&
                sessionAnswer.questionId === mostRecentAnsweredQuestion.id &&
                answer.next_question.id === currentQuestion.id
            )
        );

        if (choseAnswerWithDependency) {
          sliceValue = previousQuestionIndex - history.length + 1;
        }
      }
    }

    if (history.length > 1) {
      onSessionChange((prev) => ({
        ...prev,
        history: history.slice(0, sliceValue),
      }));
    } else {
      onPrevClick();
    }
  }, [dependencies, currentQuestion.id, history, answers, onSessionChange, onPrevClick]);

  const { readText } = useVocalisation();

  const goToNextQuestion = useCallback(async (): Promise<void> => {
    try {
      if (!currentAnswer) return;
      // INFORMATION pages write no answer row: skip the POST entirely (the payload
      // contract requires a numeric question_type_id/version pair the API's answer
      // upsert has no case for). Routing/done detection below runs unchanged.
      if (currentQuestion.type !== QuestionTypeEnum.INFORMATION) {
        await onSubmitAnswer(currentQuestion, currentAnswer);
      }

      const nextQuestion = getNextQuestion(
        questions,
        dependencies,
        currentQuestion,
        answers,
        i18n.language
      );

      // With sets, completion is "no next instance/question". The order-based
      // isQuestionnaireDone heuristic isn't instance-aware, so gate it off.
      const done = !hasSets && isQuestionnaireDone(dependencies, answers);

      if (!nextQuestion || done) {
        setResonanceLayout({});
        const totalScore = calculateQuestionnaireScore(
          questions,
          answers,
          session.is_scored
        );
        await onQuestionnaireDone?.(totalScore);
        showCompletionFeedback();
        onNextClick();
        return;
      }

      onSessionChange((prev) => ({
        ...prev,
        history: [...prev.history, nextQuestion],
      }));
    } catch (e) {
      console.log(e);
      present(e?.response?.data?.message);
    }
  }, [
    answers,
    currentAnswer,
    currentQuestion,
    dependencies,
    hasSets,
    i18n.language,
    onSessionChange,
    onSubmitAnswer,
    onQuestionnaireDone,
    present,
    questions,
    session.is_scored,
    setResonanceLayout,
    showCompletionFeedback,
  ]);

  // Keep the auto-advance timer calling the latest goToNextQuestion.
  useEffect(() => {
    goToNextRef.current = goToNextQuestion;
  });

  // Cancel any pending auto-advance when the question changes or on unmount.
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) {
        clearTimeout(autoAdvanceTimer.current);
        autoAdvanceTimer.current = null;
      }
    };
  }, [currentQuestion.id]);

  const isNextDisabled = !isAnswerValid;

  // Enter in a single-line text / number field acts as "Next" — but only when the answer is
  // valid (the same gate as the Next button), so it can't skip a required/invalid answer.
  const handleEnterNext = useCallback((): void => {
    if (!isNextDisabled) goToNextQuestion();
  }, [isNextDisabled, goToNextQuestion]);
  // With follow-up sets the flow expands beyond `questions.length` (N×set members),
  // so use an instance-aware denominator; clamp as a safety net. Set-free
  // questionnaires keep the exact original calculation.
  const progressTotal = useMemo(
    () => (hasSets ? countFlowQuestions(questions, answers) : questions.length),
    [hasSets, questions, answers]
  );
  const progressPercent = progressTotal
    ? Math.min(100, Math.round((history.length / progressTotal) * 100))
    : 0;

  console.log(
    "[Questionnaire] rendering with question:",
    currentQuestion?.id,
    "progress:",
    progressPercent
  );

  return (
      <Container>
        <Progress percent={progressPercent} />

        {currentSectionIntro && (
          <MemoizedSectionIntro
            key={currentSectionIntro.id}
            data={currentSectionIntro.content}
          />
        )}

        {currentQuestion.runtime?.heading && (
          <InstanceHeading>{currentQuestion.runtime.heading}</InstanceHeading>
        )}

        <MemoizedQuestion
          ruleMessage={translatedRuleMsg}
          answer={currentAnswer?.value}
          question={currentQuestion}
          onAnswerChange={changeAnswer}
          onEnterNext={handleEnterNext}
          readText={readText}
          // When a set heading or a section intro sits above, pull the card up under it
          // so they read as one group; otherwise keep the card's default 30px-top spacing.
          cardMargin={
            currentQuestion.runtime?.heading || currentSectionIntro
              ? "8px 20px 30px"
              : undefined
          }
        />

        <MemoizedNextPrevButtons
          disableNext={isNextDisabled}
          onPreviousClick={goToPrevQuestion}
          onNextClick={goToNextQuestion}
        />
      </Container>
  );
};

/** 🔹 Memoized Progress Component */
const Progress = React.memo(({ percent }: { percent: number }) => (
  <ProgressContainer>
    <ProgressLabel>{percent}%</ProgressLabel>
    <ProgressBarWrapper>
      <ProgressFill $width={percent} />
    </ProgressBarWrapper>
  </ProgressContainer>
));

/** 🔹 Memoized children */
const MemoizedQuestion = React.memo(Question);
const MemoizedNextPrevButtons = React.memo(NextPrevButtons);

/** 🔹 Section intro shown above the question (read-only rich text). */
const MemoizedSectionIntro = React.memo(
  ({ data }: { data: RichContentData }): ReactElement => (
    <SectionIntroCard>
      <ContentRenderer data={data} />
    </SectionIntroCard>
  )
);

const SectionIntroCard = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  /* Edges aligned to the question card (20px) and no bottom gap, so the section binds
     to the card below it; the card's top margin is reduced to sit right beneath (see
     cardMargin). */
  margin: 0 20px 0;
  padding: 4px 14px;
  background: rgba(var(--app-color-surface-rgb, 205, 235, 243), 0.35);
  border: 1px solid var(--app-color-surface, #cdeceb);
  border-radius: 12px;
`;

/**
 * Per-instance heading shown above a repeated/option set question ("First MRI"…).
 * Kept separate from the section intro, but shares its card surface (background,
 * border, radius, spacing) for visual consistency; bold teal text marks it as a heading.
 */
const InstanceHeading = styled.div`
  /* Detach from the progress bar (gap above) and bind to the question card it heads:
     bottom margin 0 + 20px side margins so its edges line up with the card, whose top
     margin is reduced to sit right beneath it (see cardMargin on the question below). */
  margin: 20px 20px 0;
  padding: 4px 14px;
  font-weight: 600;
  /* Emphasis = the strong tone derived from primary (tokens.ts) — primary
     itself may be a pastel chrome pick, too light for heading ink. */
  color: var(--app-color-emphasis, var(--app-color-primary, #0c6e6c));
  background: rgba(var(--app-color-surface-rgb, 205, 235, 243), 0.35);
  border: 1px solid var(--app-color-surface, #cdeceb);
  border-radius: 12px;
`;

const ProgressContainer = styled.div`
  width: 100%;
  padding: 10px 16px;
`;

const ProgressLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  text-align: right;
  margin-bottom: 4px;
`;

const ProgressBarWrapper = styled.div`
  background-color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.08);
  border-radius: 10px;
  height: 12px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $width?: number }>`
  /* Progress fills sweep from the emphasis tone (strong, derived from
     primary) at the leading edge to its pale -soft end — the visible
     gradient the historical bar had. */
  background: linear-gradient(
    270deg,
    var(--app-color-emphasis, var(--app-color-primary, #2db6b4)) 0%,
    var(--app-color-emphasis-soft, var(--app-color-primary-tint, #abf0ef)) 105%
  );
  height: 100%;
  border-radius: 10px;
  width: ${(props) => props.$width}%;
  transition: width 0.4s ease-in-out;
`;

export default Questionnaire;
